import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { CroppedImage } from '../components/common/ImageCropUploader';
import { CategoryIcon } from '../lib/categoryIcons';
import { ALLERGEN_LABEL } from '../lib/allergens';
import { DietIcons } from '../lib/dietTags';
import { fmtEur } from '../lib/constants';
import { LANGUAGES, LANGUAGE_CODES } from '../i18n/languages';
import { RemoteOrderProvider, RemoteCart, AddToCartButton } from '../components/public/RemoteOrder';
import { OrderingIntro } from '../components/public/OrderingIntro';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { UtensilsCrossed, Loader2, ChevronRight, ArrowLeft, Check, Globe, ShoppingBag, Bike, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MENU_LANG_KEY = 'rmp_menu_lang';

const ProductRow = ({ p, index = 0 }) => {
  const { t } = useTranslation();
  return (
    <article
      className="rmp-card-in group relative flex gap-3.5 rounded-3xl border border-border bg-card p-3 shadow-sm-token transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md-token hover:border-primary/30"
      style={{ animationDelay: `${Math.min(index, 12) * 45}ms` }}
      data-testid="menu-product"
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-3">
          <h4 className="font-display text-base font-extrabold leading-tight">{p.name}</h4>
          {!p.image && <span className="shrink-0 font-display text-base font-black text-primary tabular">{fmtEur(p.price)}</span>}
        </div>
        {p.image && <span className="mt-0.5 inline-block font-display text-base font-black text-primary tabular">{fmtEur(p.price)}</span>}
        {p.description && <p className="mt-1 text-sm leading-snug text-muted-foreground line-clamp-3">{p.description}</p>}
        {p.diet_tags?.length > 0 && (
          <div className="mt-1.5" data-testid="menu-product-diet">
            <DietIcons tags={p.diet_tags} size={18} />
          </div>
        )}
        {p.ingredients?.length > 0 && (
          <p className="mt-1.5 text-xs text-muted-foreground line-clamp-2"><span className="font-semibold text-foreground/70">{t('publicMenu.ingredients')}:</span> {p.ingredients.join(', ')}</p>
        )}
        {p.variants?.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1">
            {p.variants.map((v) => (
              <span key={v.name} className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium">{v.name}{Number(v.price_delta) ? ` +${fmtEur(v.price_delta)}` : ''}</span>
            ))}
          </div>
        )}
        {p.allergens?.length > 0 && (
          <div className="mt-2 flex flex-wrap items-center gap-1">
            {p.allergens.map((a) => (
              <span key={a} className="rounded-full bg-accent/10 px-2 py-0.5 text-[11px] font-medium text-accent">{t(`allergens.${a}`, { defaultValue: ALLERGEN_LABEL[a] || a })}</span>
            ))}
          </div>
        )}
        {!p.image && <AddToCartButton product={p} />}
      </div>
      {p.image && (
        <div className="relative shrink-0 self-center">
          <CroppedImage data={p} aspect="1 / 1" className="w-28 rounded-2xl ring-1 ring-black/5 sm:w-32" />
          <AddToCartButton product={p} variant="icon" />
        </div>
      )}
    </article>
  );
};

const MenuLanguagePicker = ({ onChange, busy }) => {
  const { i18n } = useTranslation();
  const current = LANGUAGES.find((l) => l.code === i18n.language) || LANGUAGES[0];
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid="menu-language-switcher"
          className="inline-flex items-center gap-1.5 rounded-xl border border-border bg-card px-2.5 h-9 text-sm font-medium hover:bg-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label="language"
        >
          <span className="text-base leading-none" aria-hidden>{current.flag}</span>
          {busy ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /> : <Globe className="h-4 w-4 text-muted-foreground" />}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44">
        {LANGUAGES.map((l) => (
          <DropdownMenuItem
            key={l.code}
            data-testid={`menu-language-option-${l.code}`}
            onClick={() => onChange(l.code)}
            className="gap-2 cursor-pointer"
          >
            <span className="text-base leading-none" aria-hidden>{l.flag}</span>
            <span className="flex-1">{l.label}</span>
            {current.code === l.code && <Check className="h-4 w-4 text-primary" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default function DigitalMenuPage({ channel = 'menu' }) {
  const { restaurantId } = useParams();
  const [searchParams] = useSearchParams();
  const tableParam = channel === 'menu' ? (searchParams.get('t') || searchParams.get('tavolo') || null) : null;
  const { t, i18n } = useTranslation();
  const [data, setData] = useState(null);
  const [state, setState] = useState('loading'); // loading | ok | error
  const [activeId, setActiveId] = useState(null);
  const [activeSubId, setActiveSubId] = useState(null);
  const [translating, setTranslating] = useState(false);
  const [tableInfo, setTableInfo] = useState(null); // { id, name } when QR carries a table
  const [offsiteConfig, setOffsiteConfig] = useState(null);
  const [chosenType, setChosenType] = useState(null);
  const sourceLangRef = useRef(null);

  const fetchMenu = useCallback(async (lang) => {
    const qs = lang ? `?lang=${encodeURIComponent(lang)}` : '';
    const { data } = await axios.get(`${BACKEND_URL}/api/public/menu/${restaurantId}${qs}`);
    return data;
  }, [restaurantId]);

  // Initial load: resolve the customer's language FIRST, then do a single fetch
  // already in that language. This avoids briefly showing the source language
  // (e.g. Spanish) before switching to the selected one.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        let saved = null;
        try { saved = localStorage.getItem(MENU_LANG_KEY); } catch (e) { saved = null; }
        const browser = (navigator.language || '').slice(0, 2).toLowerCase();
        const preferred = (saved && LANGUAGE_CODES.includes(saved))
          ? saved
          : (LANGUAGE_CODES.includes(browser) ? browser : null);

        if (preferred) {
          // Known preference: fetch directly in that language (backend skips
          // translation when it already matches the source).
          i18n.changeLanguage(preferred);
          const data = await fetchMenu(preferred);
          if (cancelled) return;
          sourceLangRef.current = (data?.restaurant?.language || 'it').toLowerCase();
          setData(data);
        } else {
          // No preference: show the restaurant's own (original) language.
          const data = await fetchMenu(null);
          if (cancelled) return;
          const src = (data?.restaurant?.language || 'it').toLowerCase();
          sourceLangRef.current = src;
          if (LANGUAGE_CODES.includes(src)) i18n.changeLanguage(src);
          setData(data);
        }
        setState('ok');
      } catch (e) {
        if (!cancelled) setState('error');
      }
    })();
    return () => { cancelled = true; };
  }, [fetchMenu, i18n]);

  const changeLanguage = useCallback(async (code) => {
    i18n.changeLanguage(code);
    try { localStorage.setItem(MENU_LANG_KEY, code); } catch (e) { /* ignore */ }
    const needsTranslation = code !== (sourceLangRef.current || 'it');
    setTranslating(true);
    try {
      const fresh = await fetchMenu(needsTranslation ? code : null);
      setData(fresh);
    } catch (e) { /* keep current */ } finally { setTranslating(false); }
  }, [fetchMenu, i18n]);

  const openCategory = (id) => { setActiveId(id); setActiveSubId(null); window.scrollTo({ top: 0, behavior: 'smooth' }); };
  const backToCategories = () => { setActiveId(null); setActiveSubId(null); window.scrollTo({ top: 0, behavior: 'smooth' }); };
  const openSubcategory = (id) => { setActiveSubId(id); window.scrollTo({ top: 0, behavior: 'smooth' }); };
  const backToSubcategories = () => { setActiveSubId(null); window.scrollTo({ top: 0, behavior: 'smooth' }); };

  // Resolve the table carried by a per-table QR (?t=tableId) to show its name.
  useEffect(() => {
    if (!tableParam) { setTableInfo(null); return; }
    let cancelled = false;
    axios.get(`${BACKEND_URL}/api/public/table/${restaurantId}/${tableParam}`)
      .then(({ data }) => { if (!cancelled) setTableInfo(data); })
      .catch(() => { if (!cancelled) setTableInfo(null); });
    return () => { cancelled = true; };
  }, [restaurantId, tableParam]);

  // Load takeaway/delivery configuration (only for the dedicated ordering channel).
  useEffect(() => {
    if (channel !== 'order') return undefined;
    let cancelled = false;
    axios.get(`${BACKEND_URL}/api/offsite/config/${restaurantId}`)
      .then(({ data }) => { if (!cancelled) setOffsiteConfig(data); })
      .catch(() => { if (!cancelled) setOffsiteConfig(null); });
    return () => { cancelled = true; };
  }, [restaurantId, channel]);

  if (state === 'loading') {
    return <div className="min-h-dvh flex items-center justify-center bg-background text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin mr-2" /> {t('publicMenu.loading')}</div>;
  }
  if (state === 'error' || !data) {
    return (
      <div className="min-h-dvh flex flex-col items-center justify-center bg-background text-center px-6">
        <UtensilsCrossed className="h-12 w-12 text-muted-foreground/40 mb-3" />
        <div className="font-display text-xl font-bold">{t('publicMenu.unavailable')}</div>
        <p className="text-muted-foreground text-sm mt-1">{t('publicMenu.unavailableHint')}</p>
      </div>
    );
  }

  const { restaurant, categories } = data;
  const active = categories.find((c) => c.id === activeId) || null;
  const activeSubs = active?.subcategories || [];
  const activeSub = activeSubs.find((s) => s.id === activeSubId) || null;
  const catTotal = (c) => (c.products?.length || 0) + (c.subcategories || []).reduce((n, s) => n + (s.products?.length || 0), 0);

  return (
    <RemoteOrderProvider
      remote={channel === 'menu' ? (data?.remote_orders || { enabled: false }) : { enabled: false }}
      offsite={channel === 'order' ? (offsiteConfig || {}) : {}}
      forcedType={channel === 'order' ? chosenType : null}
      restaurantId={restaurantId}
      tableId={tableInfo?.id || null}
      tableName={tableInfo?.name || null}
    >
    {channel === 'order' && !chosenType && (
      <OrderingIntro config={offsiteConfig} restaurantName={restaurant.name} onSelect={setChosenType} />
    )}
    <div className="min-h-dvh bg-background rmp-menu-reveal" key={channel === 'order' ? (chosenType || 'intro') : 'menu'}>
      {/* Header */}
      <header className="sticky top-0 z-20 border-b border-border bg-card/90 backdrop-blur-md">
        <div className="mx-auto flex max-w-3xl items-center gap-3 px-4 py-3">
          {restaurant.logo ? (
            <img src={restaurant.logo} alt="" className="h-11 w-auto max-w-[180px] object-contain shrink-0" />
          ) : (
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary"><UtensilsCrossed className="h-6 w-6 text-primary-foreground" /></div>
          )}
          <div className="min-w-0 flex-1">
            <h1 className="font-display text-lg font-black leading-tight truncate">{restaurant.name}</h1>
            {channel === 'order' && chosenType ? (
              <button onClick={() => setChosenType(null)} data-testid="menu-change-order-type"
                className="mt-0.5 inline-flex items-center gap-1.5 rounded-full bg-primary/12 px-2 py-0.5 text-xs font-semibold text-primary transition-colors hover:bg-primary/20">
                {chosenType === 'asporto' ? <ShoppingBag className="h-3.5 w-3.5" /> : <Bike className="h-3.5 w-3.5" />}
                {t(`offsite.type.${chosenType}`)}
                <RefreshCw className="h-3 w-3 opacity-70" />
              </button>
            ) : tableInfo?.name
              ? <p className="text-xs font-semibold text-primary truncate" data-testid="menu-header-table">{t('publicMenu.order.atTable')} {tableInfo.name}</p>
              : (restaurant.address && <p className="text-xs text-muted-foreground truncate">{restaurant.address}</p>)}
          </div>
          <MenuLanguagePicker onChange={changeLanguage} busy={translating} />
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 pb-16 pt-4">
        {categories.length === 0 && (
          <div className="py-20 text-center text-muted-foreground">{t('publicMenu.noProducts')}</div>
        )}

        {/* ----- LEVEL 1: category grid ----- */}
        {!active && categories.length > 0 && (
          <>
            <div className="relative mb-6 overflow-hidden rounded-3xl p-6 text-white shadow-lg-token"
              style={{ background: 'linear-gradient(135deg, hsl(24 95% 54%) 0%, hsl(20 90% 44%) 60%, hsl(18 80% 36%) 100%)' }}>
              <span className="pointer-events-none absolute -right-10 -top-12 h-40 w-40 rounded-full bg-white/15 blur-xl" />
              <span className="pointer-events-none absolute -bottom-14 -left-8 h-36 w-36 rounded-full bg-black/10 blur-xl" />
              <div className="relative">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-white/20 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider backdrop-blur-sm">
                  {channel === 'order' && chosenType
                    ? (chosenType === 'asporto' ? <ShoppingBag className="h-3.5 w-3.5" /> : <Bike className="h-3.5 w-3.5" />)
                    : <UtensilsCrossed className="h-3.5 w-3.5" />}
                  {channel === 'order' && chosenType ? t(`offsite.type.${chosenType}`) : t('publicMenu.digitalCard')}
                </span>
                <h2 className="mt-3 font-display text-3xl font-black tracking-tight leading-none sm:text-4xl">{t('publicMenu.ourMenu')}</h2>
                <p className="mt-1.5 text-sm text-white/85">{t('publicMenu.ourMenuHint')}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3.5 sm:grid-cols-3" data-testid="menu-category-grid">
              {categories.map((c, i) => (
                <button
                  key={c.id}
                  onClick={() => openCategory(c.id)}
                  data-testid={`menu-category-${c.id}`}
                  style={{ animationDelay: `${Math.min(i, 12) * 50}ms` }}
                  className="rmp-card-in group relative h-44 overflow-hidden rounded-3xl border border-border text-left shadow-sm-token transition-all duration-200 hover:-translate-y-1 hover:shadow-lg-token active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {c.image ? (
                    <img src={c.image} alt="" className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-110" style={{ objectPosition: `${c.image_x ?? 50}% ${c.image_y ?? 50}%`, transform: `scale(${c.image_zoom ?? 1})` }} />
                  ) : (
                    <div className="absolute inset-0" style={{ background: c.color || 'hsl(var(--muted))' }} />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/35 to-transparent" />
                  <span className="absolute left-2.5 top-2.5 flex h-9 w-9 items-center justify-center rounded-xl bg-white/95 shadow-md backdrop-blur-sm">
                    <CategoryIcon iconKey={c.icon} color={c.color} className="h-5 w-5" />
                  </span>
                  <div className="absolute inset-x-0 bottom-0 p-3.5">
                    <span className="block font-display text-lg font-extrabold uppercase leading-tight tracking-wide text-white [text-shadow:0_2px_8px_rgba(0,0,0,0.85)]">{c.name}</span>
                    <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-white/15 px-2 py-0.5 text-[11px] font-semibold text-white backdrop-blur-sm transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                      {catTotal(c)} {catTotal(c) === 1 ? t('publicMenu.product') : t('publicMenu.products')} <ChevronRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </>
        )}

        {/* ----- LEVEL 2: category detail (subcategories and/or products) ----- */}
        {active && !activeSub && (
          <div data-testid="menu-category-detail">
            <button onClick={backToCategories} className="mb-4 inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5 text-sm font-semibold text-foreground shadow-sm-token hover:bg-muted" data-testid="menu-back-button">
              <ArrowLeft className="h-4 w-4" /> {t('publicMenu.allCategories')}
            </button>

            <div className="relative mb-5 h-32 overflow-hidden rounded-3xl border border-border shadow-sm-token">
              {active.image ? (
                <img src={active.image} alt="" className="absolute inset-0 h-full w-full object-cover" style={{ objectPosition: `${active.image_x ?? 50}% ${active.image_y ?? 50}%`, transform: `scale(${active.image_zoom ?? 1})` }} />
              ) : (
                <div className="absolute inset-0" style={{ background: active.color || 'hsl(var(--muted))' }} />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
              <span className="absolute left-3 top-3 flex h-11 w-11 items-center justify-center rounded-2xl bg-white/95 shadow-md backdrop-blur-sm">
                <CategoryIcon iconKey={active.icon} color={active.color} className="h-6 w-6" />
              </span>
              <h2 className="absolute inset-x-0 bottom-0 p-3.5 font-display text-3xl font-black uppercase tracking-wide text-white [text-shadow:0_2px_8px_rgba(0,0,0,0.85)]">{active.name}</h2>
            </div>

            {/* Subcategory cards (if any) */}
            {activeSubs.length > 0 && (
              <div className="mb-5 grid grid-cols-2 gap-3.5 sm:grid-cols-3" data-testid="menu-subcategory-grid">
                {activeSubs.map((s, i) => (
                  <button
                    key={s.id}
                    onClick={() => openSubcategory(s.id)}
                    data-testid={`menu-subcategory-${s.id}`}
                    style={{ animationDelay: `${Math.min(i, 12) * 50}ms` }}
                    className="rmp-card-in group relative h-40 overflow-hidden rounded-3xl border border-border text-left shadow-sm-token transition-all duration-200 hover:-translate-y-1 hover:shadow-lg-token active:scale-[0.98]"
                  >
                    {s.image ? (
                      <img src={s.image} alt="" className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-110" style={{ objectPosition: `${s.image_x ?? 50}% ${s.image_y ?? 50}%`, transform: `scale(${s.image_zoom ?? 1})` }} />
                    ) : (
                      <div className="absolute inset-0" style={{ background: s.color || 'hsl(var(--muted))' }} />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/35 to-transparent" />
                    <span className="absolute left-2.5 top-2.5 flex h-9 w-9 items-center justify-center rounded-xl bg-white/95 shadow-md backdrop-blur-sm">
                      <CategoryIcon iconKey={s.icon} color={s.color} className="h-5 w-5" />
                    </span>
                    <div className="absolute inset-x-0 bottom-0 p-3.5">
                      <span className="block font-display text-base font-extrabold uppercase leading-tight tracking-wide text-white [text-shadow:0_2px_8px_rgba(0,0,0,0.85)]">{s.name}</span>
                      <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-white/15 px-2 py-0.5 text-[11px] font-semibold text-white backdrop-blur-sm transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                        {s.products.length} {s.products.length === 1 ? t('publicMenu.product') : t('publicMenu.products')} <ChevronRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* Direct products (not assigned to any subcategory) */}
            {active.products.length > 0 && (
              <div className="space-y-3.5">
                {active.products.map((p, i) => <ProductRow key={p.id} p={p} index={i} />)}
              </div>
            )}
          </div>
        )}

        {/* ----- LEVEL 3: products of selected subcategory ----- */}
        {active && activeSub && (
          <div data-testid="menu-subcategory-detail">
            <button onClick={backToSubcategories} className="mb-4 inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5 text-sm font-semibold text-foreground shadow-sm-token hover:bg-muted" data-testid="menu-back-subcategory">
              <ArrowLeft className="h-4 w-4" /> {active.name}
            </button>

            <div className="relative mb-5 h-32 overflow-hidden rounded-3xl border border-border shadow-sm-token">
              {activeSub.image ? (
                <img src={activeSub.image} alt="" className="absolute inset-0 h-full w-full object-cover" style={{ objectPosition: `${activeSub.image_x ?? 50}% ${activeSub.image_y ?? 50}%`, transform: `scale(${activeSub.image_zoom ?? 1})` }} />
              ) : (
                <div className="absolute inset-0" style={{ background: activeSub.color || 'hsl(var(--muted))' }} />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
              <span className="absolute left-3 top-3 flex h-11 w-11 items-center justify-center rounded-2xl bg-white/95 shadow-md backdrop-blur-sm">
                <CategoryIcon iconKey={activeSub.icon} color={activeSub.color} className="h-6 w-6" />
              </span>
              <h2 className="absolute inset-x-0 bottom-0 p-3.5 font-display text-3xl font-black uppercase tracking-wide text-white [text-shadow:0_2px_8px_rgba(0,0,0,0.85)]">{activeSub.name}</h2>
            </div>

            <div className="space-y-3.5">
              {activeSub.products.map((p, i) => <ProductRow key={p.id} p={p} index={i} />)}
            </div>
          </div>
        )}

        <footer className="mt-10 border-t border-border pt-5 text-center text-xs text-muted-foreground">
          {restaurant.name}{restaurant.phone ? ` · ${restaurant.phone}` : ''}
          <div className="mt-1">{t('publicMenu.digitalCard')} · {t('common.appName')}</div>
        </footer>
      </main>
      <RemoteCart />
    </div>
    </RemoteOrderProvider>
  );
}
